import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppThunk } from '../../../app/store';
import { notesDraftActions } from '../notes/notesDraftSlice';

export type ActiveDrawer =
  | { type: 'eti'; taskId: number }
  | { type: 'notes' }
  | null;

type DrawerRequest = { type: 'eti'; taskId: number } | { type: 'notes' };

interface DrawerCoordState {
  activeDrawer: ActiveDrawer;
  etiFormDirtyByTaskId: Record<number, boolean>;
  notesOpenRequest: { taskId: number } | null;
}

const initialState: DrawerCoordState = {
  activeDrawer: null,
  etiFormDirtyByTaskId: {},
  notesOpenRequest: null,
};

const isSameDrawer = (left: ActiveDrawer, right: ActiveDrawer) => {
  if (!left || !right) return left === right;
  if (left.type !== right.type) return false;
  if (left.type === 'notes') return true;
  return right.type === 'eti' && left.taskId === right.taskId;
};

const drawerCoordSlice = createSlice({
  name: 'drawerCoord',
  initialState,
  reducers: {
    drawerOpened: (state, action: PayloadAction<DrawerRequest>) => {
      state.activeDrawer = action.payload;
    },
    drawerClosed: (state, action: PayloadAction<ActiveDrawer | undefined>) => {
      if (!action.payload || isSameDrawer(state.activeDrawer, action.payload)) {
        state.activeDrawer = null;
      }
    },
    syncEtiDirty: (
      state,
      action: PayloadAction<{ taskId: number; isDirty: boolean }>,
    ) => {
      state.etiFormDirtyByTaskId[action.payload.taskId] =
        action.payload.isDirty;
    },
    requestNotesOpen: (state, action: PayloadAction<{ taskId: number }>) => {
      state.notesOpenRequest = action.payload;
    },
    notesOpenRequestHandled: state => {
      state.notesOpenRequest = null;
    },
  },
});

export const drawerCoordActions = drawerCoordSlice.actions;
export const drawerCoordReducer = drawerCoordSlice.reducer;

export const requestOpen =
  (drawer: DrawerRequest): AppThunk =>
  (dispatch, getState) => {
    const { activeDrawer, etiFormDirtyByTaskId } = getState().drawerCoord;

    if (drawer.type === 'notes' && activeDrawer?.type === 'eti') {
      const taskId = activeDrawer.taskId;
      if (etiFormDirtyByTaskId[taskId]) {
        dispatch(drawerCoordActions.requestNotesOpen({ taskId }));
        return;
      }
    }

    dispatch(drawerCoordActions.drawerOpened(drawer));
    if (drawer.type === 'notes') {
      dispatch(notesDraftActions.openDrawer());
      return;
    }

    dispatch(notesDraftActions.closeDrawer());
  };

export const closeDrawer =
  (drawer?: ActiveDrawer): AppThunk =>
  dispatch => {
    dispatch(drawerCoordActions.drawerClosed(drawer));
    if (!drawer || drawer.type === 'notes') {
      dispatch(notesDraftActions.closeDrawer());
    }
  };

export const toggleNotesDrawer = (): AppThunk => (dispatch, getState) => {
  const { activeDrawer } = getState().drawerCoord;
  if (activeDrawer?.type === 'notes') {
    dispatch(closeDrawer({ type: 'notes' }));
    return;
  }
  dispatch(requestOpen({ type: 'notes' }));
};
